<template>
  <v-col cols="12" class="ticket-price-list" data-cy="ticketListTable">
    <v-data-table
      :headers="headers"
      :items="ticketConfig"
      :items-per-page="ticketConfig.length"
      sort-by="displayOrder"
      :no-data-text="$t('ticket.noTickets')"
      hide-default-footer
      class="ticket-table"
    >
      <!-- eslint-disable-next-line -->
      <template #item.name="{ item }">
        <b-sale-end-timer
          v-if="!configIsConditional(item) && displaySaleEndTimer(item)"
          :color="$whitelabel.lightTheme.backgroundHighlightWarning.base"
          :countdown-end="item.endSaleAt"
        ></b-sale-end-timer>
        <div v-if="configIsConditional(item)">
          <span class="MOBILE_h11_Bold">{{
            `${$t('tickets.availableWith')} ${conditionalConfigText(item)}`
          }}</span>
        </div>
        <span class="item-styling">
          {{ item.name }}
        </span>
      </template>
      <!-- eslint-disable-next-line -->
      <template #item.icon="{ item }">
        <span>
          <v-icon
            class="icon-styling"
            :disabled="!item.description"
            @click="showTicketInfoDialog(item)"
          >
            mdi-information
          </v-icon>
        </span>
      </template>
      <!-- eslint-disable-next-line -->
      <template #item.availableTickets="{ item }">
        <span
          :class="{
            'sold-out':
              isSoldOut(item) ||
              isCanceledEvent ||
              !discountAvailable(item.discount) ||
              timeslotSoldOut ||
              !ticketConfigIsSelectable(item),
            'availability-status': true
          }"
        >
          {{ ticketAvailabilityLevel(item) }}
        </span>
      </template>
      <!-- eslint-disable-next-line -->
      <template #item.faceValue="{ item }">
        <span
          v-if="!isDiscounted(item)"
          :class="{
            'sold-out': isSoldOut(item) || isCanceledEvent || timeslotSoldOut,
            'price text-left': true
          }"
        >
          {{ formatTicketConfigLabel(item, item.discount) }}
        </span>
        <BSelect
          v-else
          class="select--small"
          :placeholder="$t('main.select')"
          match-content-width
          :options="availableDiscounts(item)"
          @input="onItemChange(item, $event)"
        />
      </template>
      <!-- eslint-disable-next-line -->
      <template #item.actions="{ item }">
        <BButton
          class="button--small button--uppercase"
          fill
          height="2.5rem"
          data-cy="addButton"
          :outlined="isDisabled(item)"
          :disabled="isDisabled(item)"
          @click.native="add(item)"
        >
          {{ $t('buyTickets.add') }}
        </BButton>
      </template>
    </v-data-table>
    <v-dialog
      v-model="showInfoDialog"
      max-width="36.375rem"
      overlay-opacity="0.8"
      overlay-color="backgroundPrimary"
    >
      <v-card class="d-flex flex-column">
        <v-card-title class="description">{{
          selectedTicketConfigName
        }}</v-card-title>
        <v-divider class="mx-4"></v-divider>
        <v-card-text class="scrollable-content">
          <div
            class="my-4 description"
            v-html="$sanitize(selectedTicketConfigDescription)"
          ></div>
        </v-card-text>
        <v-card-actions>
          <v-col class="text-right">
            <v-btn @click="showInfoDialog = false">
              {{ $t('dialog.close') }}
            </v-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import moment from 'moment';
import BButton from '../general/BButton';
import BSelect from '../general/BSelect.vue';
import { EVENT_STATES } from '~/utils/utils';
import BSaleEndTimer from '~/components/general/BSaleEndTimer.vue';
import { applyDiscount } from '~/utils/pricing';
import { ConditionalTicketValidator } from '~/utils/order/conditionals';

export default {
  name: 'TicketPriceList',
  components: { BButton, BSelect, BSaleEndTimer },
  props: {
    ticketConfig: {
      type: Array,
      default() {
        return [];
      }
    },
    ticketDiscounts: {
      type: Array,
      default() {
        return [];
      }
    },
    eventStatus: {
      type: String,
      default: ''
    },
    ownedTickets: {
      type: Array,
      default() {
        return [];
      }
    },
    selectedTickets: {
      type: Array,
      default() {
        return [];
      }
    },
    selectedTimeslot: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      headers: [
        {
          text: this.$t('buyTickets.ticketConfigName').toUpperCase(),
          value: 'name',
          sortable: false,
          width: '35%',
          class: 'header-styling'
        },
        {
          text: this.$t('buyTickets.info').toUpperCase(),
          value: 'icon',
          width: '15%',
          sortable: false,
          class: 'header-styling'
        },
        {
          text: this.$t('buyTickets.ticketConfigAvail').toUpperCase(),
          value: 'availableTickets',
          sortable: false,
          class: 'header-styling'
        },
        {
          text: this.$t('buyTickets.ticketConfigPrice').toUpperCase(),
          value: 'faceValue',
          width: '30%',
          sortable: true,
          class: 'header-styling'
        },
        {
          value: 'actions',
          sortable: false,
          align: 'right',
          class: 'header-styling'
        }
      ],
      donationDialog: false,
      buttonIsDisabled: {},
      showInfoDialog: false,
      selectedTicketConfigName: '',
      selectedTicketConfigDescription: '',
      selectedTicketConfig: this.ticketConfig,
      eventStates: EVENT_STATES,
      validator: new ConditionalTicketValidator()
    };
  },
  computed: {
    // Checks if the payment is made using a cryptocurrency.
    isCryptoPayment() {
      return this.$whitelabel.supportedCryptoCurrencies.includes(this.currency);
    },

    // Returns the number of fraction digits to be displayed based on the payment method.
    fractionDigits() {
      return this.isCryptoPayment ? 8 : 2;
    },
    isCanceledEvent() {
      return this.eventStatus === this.eventStates.CANCELED;
    },
    eventInDraft() {
      return this.eventStatus === this.eventStates.DRAFT;
    },
    isSmallDevice() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    isSoldOut() {
      return (item) => {
        return (
          item.availableTickets <= 0 ||
          item.quantity <= 0 ||
          item.ended ||
          !item.started
        );
      };
    },
    isDiscountSoldOut() {
      return (discount) => {
        const foundDiscount = this.findDiscount(discount?.id);
        return foundDiscount?.availableTickets <= 0;
      };
    },
    /** Controls whether or not the add ticket button should be disabled */
    isDisabled() {
      return (ticketConfig) => {
        return (
          (ticketConfig.discount &&
            ticketConfig.discount.selectedQuantity ===
              ticketConfig.ticketsPerUser) ||
          this.isCanceledEvent ||
          this.eventInDraft ||
          this.isSoldOut(ticketConfig) ||
          this.isDiscountSoldOut(ticketConfig.discount) ||
          this.buttonIsDisabled[ticketConfig.id] ||
          !this.ticketConfigIsSelectable(ticketConfig) ||
          this.timeslotSoldOut
        );
      };
    },
    conditionalTicketingSatisfied() {
      // Validate if the order would be valid if we added a ticket of `ticketConfig` type
      // to the combination of tickets in cart and already owned tickets
      return (ticketConfig) => {
        if (!this.configIsConditional(ticketConfig)) {
          return true;
        }
        const canAddTicket = this.validator.canAddTicket(
          ticketConfig,
          this.ownedTickets,
          this.selectedTickets
        );
        return canAddTicket;
      };
    },
    timeslotHasLimitedCapacity() {
      return this.selectedTimeslot?.ticketConfigCapacity > 0;
    },
    ticketConfigCapacityReached() {
      if (!this.selectedTimeslot || !this.timeslotHasLimitedCapacity) {
        return false;
      }
      const selectedTicketsIds = this.getSelectedTicketsInTimeslot;
      const ticketConfigIds = [
        ...new Set([
          ...selectedTicketsIds,
          ...this.selectedTimeslot.associatedTicketConfig
        ])
      ];
      return (
        ticketConfigIds?.length === this.selectedTimeslot.ticketConfigCapacity
      );
    },
    getSelectedTicketsInTimeslot() {
      return this.selectedTickets
        .filter(
          (selectedTicket) =>
            selectedTicket.timeslotId === this.selectedTimeslot?.id
        )
        .map((selectedTicket) => selectedTicket.id);
    },
    ticketConfigIsSelectable() {
      return (ticketConfig) => {
        if (!this.ticketConfigCapacityReached) {
          return true;
        }
        const selectedTicketsIds = this.getSelectedTicketsInTimeslot;
        const ticketConfigAlreadyInTimeslot =
          this.selectedTimeslot.associatedTicketConfig.includes(
            ticketConfig.id
          );
        const ticketConfigAlreadyInCart = selectedTicketsIds.includes(
          ticketConfig.id
        );
        return ticketConfigAlreadyInTimeslot || ticketConfigAlreadyInCart;
      };
    },
    timeslotSoldOut() {
      return (
        this.selectedTimeslot?.availableTickets -
          this.getSelectedTicketsInTimeslot?.length <=
        0
      );
    }
  },
  watch: {
    ticketConfig(newValue) {
      this.selectedTicketConfig = newValue;
    }
  },
  mounted() {
    // first disable 'add' buttons until a discount is selected
    this.disableButtons();
  },
  methods: {
    discountIsUsable(discount) {
      return discount?.availableTickets >= discount?.minQuantity;
    },
    availableDiscounts(tc) {
      const discounts = tc.discounts.filter(
        (td) =>
          td.availableTickets === undefined ||
          (td?.availableTickets > 0 &&
            this.discountIsUsable(td) &&
            this.discountAvailable(td))
      );
      // map discounts to options
      return discounts.map((discount) => {
        return {
          id: discount.id,
          displayName: this.formatTicketConfigLabel(tc, discount)
        };
      });
    },
    // checks if user didn't select more tickets that they can buy at this point
    maxNotReached(tc) {
      return tc.ticketsPerUser - tc.userHolds - tc.amountOfTicketInCart > 0;
    },
    add(item) {
      const ticketConfig = this.selectedTicketConfig.find(
        (tc) => tc.id === item.id
      );
      if (this.configIsConditional(ticketConfig) && !this.$auth.loggedIn) {
        this.$emit('openLogin');
        return;
      }
      if (!this.conditionalTicketingSatisfied(ticketConfig)) {
        this.$notifier.showMessage({
          content: this.$t('conditional.error.message', {
            ticket: this.conditionalConfigText(ticketConfig)
          }),
          type: 'error'
        });
        return;
      }
      this.$emit('add', {
        ticket: ticketConfig,
        discount: ticketConfig.discount
      });
    },
    showTicketInfoDialog(item) {
      this.showInfoDialog = true;
      this.selectedTicketConfigName = item.name;
      this.selectedTicketConfigDescription = item.description;
    },
    findDiscount(discountId) {
      return this.ticketDiscounts.find((td) => td.id === discountId);
    },
    onItemChange(ticketConfig, id) {
      const index = this.ticketConfig.findIndex(
        (tc) => tc.id === ticketConfig.id
      );

      this.selectedTicketConfig[index].discount =
        ticketConfig.discounts.find((td) => td.id === id) || null;
      this.$set(this.buttonIsDisabled, ticketConfig.id, false);
      if (!this.discountAvailable(this.selectedTicketConfig[index].discount)) {
        this.$set(this.buttonIsDisabled, ticketConfig.id, true);
      }
    },
    discountAvailable(discount) {
      const now = moment();
      if (!discount?.id) {
        return true;
      }
      return (
        now.isSameOrAfter(moment(discount.startAt)) &&
        now.isSameOrBefore(moment(discount.endAt))
      );
    },
    isDiscounted(item) {
      return item?.discounts?.length > 1;
    },
    ticketAvailabilityLevel(ticketConfig) {
      if (
        this.isCanceledEvent ||
        ticketConfig.ended ||
        !this.ticketConfigIsSelectable(ticketConfig)
      ) {
        return this.$t('cart.notAvailable');
      }
      if (ticketConfig.availableTickets <= 0) {
        if (this.maxNotReached(ticketConfig)) {
          return this.$t('cart.soldOut').toUpperCase();
        } else {
          return this.$t('buyTickets.maxReached').toUpperCase();
        }
      }
      if (!ticketConfig.started) {
        return this.$t('cart.ticketSaleNotStarted');
      }
      const now = moment();
      if (
        ticketConfig.discount?.startAt &&
        now.isBefore(moment(ticketConfig.discount.startAt))
      ) {
        return this.$t('cart.discountNotStarted');
      }
      if (
        ticketConfig.discount?.endAt &&
        now.isAfter(moment(ticketConfig.discount?.endAt))
      ) {
        return this.$t('cart.discountEnded');
      }
      if (this.timeslotSoldOut) {
        return this.$t('cart.soldOut').toUpperCase();
      }
      return this.$t('cart.available');
    },
    formatTicketConfigLabel(ticketConfig, discount) {
      const orderItemPrice = applyDiscount(
        discount,
        ticketConfig.faceValue
      ).toFixed(this.fractionDigits);

      return `${orderItemPrice} ${ticketConfig?.currency} - ${discount?.name}`;
    },
    disableButtons() {
      this.ticketConfig.forEach((tc) => {
        if (this.isDiscounted(tc)) {
          this.$set(this.buttonIsDisabled, tc.id, true);
        }
      });
    },
    displaySaleEndTimer(config) {
      const endingMoment = moment(config.endSaleAt);
      const now = moment();
      const endsSoon = endingMoment.diff(now, 'days') <= 30;

      return config.showSaleEndTimer && this.configOnSale(config) && endsSoon;
    },
    configOnSale(config) {
      return config.started && !config.ended;
    },
    configIsConditional(config) {
      return config?.dependsOn?.length > 0;
    },
    conditionalConfigText(config) {
      return config?.dependsOn?.map((tc) => tc?.name).join(', ');
    }
  }
};
</script>

<style lang="scss">
@import 'assets/styles/fonts.scss';
p {
  font-size: 1rem;
  line-height: 140%;
  font-weight: 400;
  color: var(--v-textPrimary-base);
  margin-bottom: 0;
}
.title {
  line-height: 200%;
  color: var(--v-textPrimary-base);
}

.title.sold-out {
  color: #6b6b6b;
}

.price {
  text-align: right;
  line-height: 200%;
  color: var(--v-textPrimary-base);
}

.price.sold-out {
  color: var(--v-textHighlightError-base) !important;
}
.sold-out {
  color: var(--v-textHighlightError-base) !important;
}
.flex-extend {
  flex-basis: 100%;
}
.no-height {
  height: 0;
}

.header-styling {
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  color: var(--v-primary-lighten3) !important;
  border-bottom: none !important;
  word-break: break-word;
}

.availability-status {
  color: var(--v-textPrimary-base);
}

tbody {
  background-color: var(--v-backgroundPrimary-lighten5);
  tr:hover {
    background-color: transparent !important;
  }
}

.discount-disabled {
  input::placeholder {
    color: var(--v-textHighlightError-base) !important;
  }
}

.description {
  color: var(--v-textPrimary-base);
}

.discount-select {
  .v-input {
    line-height: 120%;
  }
  .v-input__control {
    font-size: 1rem;
    min-height: 2.5rem !important;
    @media (max-width: 600px) {
      min-height: 40px !important;
    }
  }
  .v-input__slot {
    background-color: var(--v-backgroundPrimary-lighten5) !important;
    // in case you need to remove the left padding from the placeholder
    // padding-left: 0% !important;
  }
  svg path {
    stroke: var(--v-textPrimary-base);
  }
  .v-select__selection {
    color: var(--v-textPrimary-base) !important;
    margin-bottom: 0;
  }
  .v-select__selection--comma {
    margin: 0;
    flex: 1 1 auto;
    font-size: 1rem;
    line-height: 120%;
    display: inline-block;
  }
  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0.25rem;
  }
}
// if needed again:
// .discount-select.v-input--is-focused {
//   svg path {
//     stroke: white;
//   }
//   .v-select__selection {
//     color: white !important;
//   }
// }
.discount-select.quantity {
  .v-select__selection--comma {
    min-width: 1.75rem;
  }
  input {
    padding: 0;
  }
  .v-input__slot {
    padding-right: 4px !important;
  }
  .v-input__append-inner {
    height: 2.5rem;
    @media (max-width: 600px) {
      min-height: 40px !important;
    }
  }
}

.icon-styling {
  color: var(--v-backgroundPrimary-base) !important;
}

.ticket-table {
  &.v-data-table {
    tbody {
      tr {
        td {
          padding-block: 14px;
        }
      }
    }
  }
  .v-data-table {
    &__wrapper {
      border-radius: 4px;
    }
  }
}

.scrollable-content {
  flex-grow: 1;
  overflow-y: auto;
  height: 464px;
}

.dialog {
  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }
}
</style>
